import { format, isSameMonth } from 'date-fns';
import type { AvailabilityRule, DateSpecificAvailability, PTODay } from '../hooks/useAvailability';

interface DayCellProps {
  date: Date;
  standardRule: AvailabilityRule | null;
  dateOverride: DateSpecificAvailability | null;
  pto: PTODay | null;
  isToday: boolean;
  currentMonth: Date;
  onSelect: (date: Date, event: React.MouseEvent) => void;
}

export function DayCell({
  date,
  standardRule,
  dateOverride,
  pto,
  isToday,
  currentMonth,
  onSelect,
}: DayCellProps) {
  const isCurrentMonth = isSameMonth(date, currentMonth);

  // Determine the effective status and hours
  const hasPto = pto !== null;
  const hasOverride = dateOverride !== null;
  const hasStandardHours = standardRule !== null;
  const isWorking = !hasPto && (hasOverride || hasStandardHours);

  // Build tooltip text
  const getTooltipText = (): string => {
    if (hasPto) {
      return `PTO${pto.reason ? `: ${pto.reason}` : ''}`;
    }
    if (hasOverride) {
      return `Custom: ${dateOverride.startTime.substring(0, 5)} - ${dateOverride.endTime.substring(0, 5)}`;
    }
    if (hasStandardHours) {
      return `Standard: ${standardRule.startTime.substring(0, 5)} - ${standardRule.endTime.substring(0, 5)}`;
    }
    return 'No hours scheduled';
  };

  // Determine background color
  const getBgClass = (): string => {
    if (!isCurrentMonth) {
      return 'bg-background-muted/50';
    }
    if (hasPto) {
      return 'bg-warning/20';
    }
    if (isWorking) {
      return 'bg-success/10';
    }
    return 'bg-background';
  };

  // Determine text/indicator color
  const getIndicatorClass = (): string => {
    if (hasPto) {
      return 'bg-warning';
    }
    if (isWorking) {
      return 'bg-success';
    }
    return 'bg-gray-300';
  };

  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(date, e);
      }}
      title={getTooltipText()}
      className={`
        relative flex flex-col items-center justify-start p-1.5 min-h-[56px] w-full
        border-r border-b border-border last:border-r-0
        hover:ring-2 hover:ring-primary hover:ring-inset
        transition-colors cursor-pointer
        ${getBgClass()}
        ${!isCurrentMonth ? 'opacity-50' : ''}
      `}
    >
      {/* Date number */}
      <span
        className={`
          text-sm font-medium leading-none
          ${isToday ? 'bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center' : ''}
          ${!isToday && isCurrentMonth ? 'text-foreground' : ''}
          ${!isToday && !isCurrentMonth ? 'text-foreground-muted' : ''}
        `}
      >
        {format(date, 'd')}
      </span>

      {/* Status indicator dot */}
      <div className="mt-1 flex items-center gap-0.5">
        <div className={`w-2 h-2 rounded-full ${getIndicatorClass()}`} />

        {/* Override badge */}
        {hasOverride && !hasPto && (
          <div className="w-1.5 h-1.5 rounded-full bg-primary" title="Custom hours" />
        )}
      </div>

      {/* Compact time display for working days */}
      {isWorking && isCurrentMonth && (
        <span className="text-[10px] text-foreground-muted mt-0.5 leading-none">
          {hasOverride
            ? dateOverride.startTime.substring(0, 5)
            : standardRule?.startTime.substring(0, 5)}
        </span>
      )}

      {/* PTO label */}
      {hasPto && isCurrentMonth && (
        <span className="text-[10px] text-warning-foreground font-medium mt-0.5 leading-none">
          PTO
        </span>
      )}
    </button>
  );
}
